/* Lavish Pakistan theme behaviour.
   Cart, add-to-cart, drawer and predictive search are Dawn's own scripts (cart.js, cart-drawer.js,
   product-form.js, predictive-search.js). This file only adds the editorial UI from the approved
   mockup: dialogs, header-over-hero, reveals, collection filter/sort, product gallery, lightbox,
   the Type + Size picker (which resolves to a real Shopify variant) and the sticky buy bar. */
(() => {
  'use strict';

  const root = document.documentElement;
  root.classList.remove('no-js');
  root.classList.add('js');

  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const cfg = window.lavish || {};

  /* ------------------------------------------------------------------ dialogs */
  function openDlg(d) {
    if (!d || d.open) return;
    d.showModal();
    requestAnimationFrame(() => requestAnimationFrame(() => d.classList.add('is-open')));
  }
  function closeDlg(d) {
    if (!d || !d.open) return;
    d.classList.remove('is-open');
    if (reduce.matches) d.close();
    else setTimeout(() => { if (d.open && !d.classList.contains('is-open')) d.close(); }, 340);
  }
  function bindDialog(d) {
    if (d.dataset.lvBound) return;
    d.dataset.lvBound = '1';
    d.addEventListener('cancel', (e) => { e.preventDefault(); closeDlg(d); });
    d.addEventListener('close', () => d.classList.remove('is-open'));
    d.addEventListener('click', (e) => {
      if (e.target === d || e.target.hasAttribute('data-backdrop')) closeDlg(d);
    });
  }
  $$('dialog').forEach(bindDialog);

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open]');
    if (opener) {
      e.preventDefault();
      const target = document.getElementById(opener.getAttribute('data-open'));
      const current = opener.closest('dialog');
      if (current && current !== target) closeDlg(current);
      openDlg(target);
      if (target && target.id === 'search') setTimeout(() => { const i = $('#search-input'); if (i) i.focus(); }, 80);
      return;
    }

    const cartOpener = e.target.closest('[data-open-cart]');
    if (cartOpener) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        e.preventDefault();
        closeDlg(cartOpener.closest('dialog'));
        setTimeout(() => drawer.open(cartOpener), reduce.matches ? 0 : 340);
      }
      return;
    }

    const closer = e.target.closest('[data-close]');
    if (closer) closeDlg(closer.closest('dialog'));

    const menuLink = e.target.closest('#menu a[href]');
    if (menuLink && menuLink.getAttribute('href').startsWith('#')) closeDlg($('#menu'));
  });

  /* ---------------------------------------------------- header over hero (home) */
  const header = $('[data-header]');
  const hero = $('[data-hero]');
  if (header && hero && header.dataset.overlay !== 'false') {
    const update = () => header.classList.toggle('is-over-hero', window.scrollY < hero.offsetHeight - header.offsetHeight - 8);
    update();
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { update(); ticking = false; });
    }, { passive: true });
    window.addEventListener('resize', update);
  }

  /* ------------------------------------------------------------ scroll reveals */
  function initReveals() {
    const reveals = $$('.reveal:not(.in), .reveal-img:not(.in)');
    if (!reveals.length) return;
    if (!('IntersectionObserver' in window) || reduce.matches || root.classList.contains('shopify-design-mode')) {
      reveals.forEach((el) => el.classList.add('in'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('in'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    reveals.forEach((el) => io.observe(el));
    setTimeout(() => reveals.forEach((el) => el.classList.add('in')), 2500); // never leave content hidden
  }
  initReveals();
  document.addEventListener('shopify:section:load', initReveals);

  /* -------------------------------------------- collection: filter and sorting */
  function initCollection() {
    const grid = $('[data-grid]');
    if (!grid || grid.dataset.lvBound) return;
    grid.dataset.lvBound = '1';
    const cards = $$('.card', grid);
    const chips = $$('[data-filter]');
    const sort = $('[data-sort]');
    const count = $('[data-count]');
    const empty = $('[data-empty]');
    const word = count ? count.dataset.word || '' : '';
    let group = 'all';
    const apply = () => {
      const mode = sort ? sort.value : 'featured';
      const ordered = cards.slice().sort((a, b) => {
        if (mode === 'low') return a.dataset.price - b.dataset.price || a.dataset.index - b.dataset.index;
        if (mode === 'high') return b.dataset.price - a.dataset.price || a.dataset.index - b.dataset.index;
        return a.dataset.index - b.dataset.index;
      });
      let shown = 0;
      ordered.forEach((c, i) => {
        c.style.order = i;
        const on = group === 'all' || c.dataset.group === group;
        c.hidden = !on;
        if (on) shown++;
      });
      if (count && !count.dataset.static) count.textContent = shown + ' ' + (count.dataset.designs || 'designs');
      if (empty) empty.hidden = shown !== 0;
    };
    chips.forEach((ch) => ch.addEventListener('click', () => {
      group = ch.dataset.filter;
      chips.forEach((c) => c.setAttribute('aria-pressed', String(c === ch)));
      apply();
    }));
    if (sort) sort.addEventListener('change', apply);
    apply();
  }
  initCollection();

  /* ---------------------------------------------------------- product page */
  function initProduct() {
    const rootEl = $('[data-product-root]');
    if (!rootEl || rootEl.dataset.lvBound) return;
    rootEl.dataset.lvBound = '1';

    /* ---- gallery ---- */
    const track = $('[data-track]', rootEl);
    if (track) {
      const slides = $$('[data-slide]', track);
      const counter = $('[data-counter]', rootEl);
      const dotsBox = $('[data-dots]', rootEl);
      const prev = $('[data-g-prev]', rootEl);
      const next = $('[data-g-next]', rootEl);
      let index = 0;
      const dots = slides.map((_, i) => {
        const d = document.createElement('button');
        d.type = 'button';
        d.className = 'dot';
        d.setAttribute('aria-label', `${i + 1} / ${slides.length}`);
        d.addEventListener('click', () => goTo(i));
        if (dotsBox) dotsBox.appendChild(d);
        return d;
      });
      const isCarousel = () => track.scrollWidth > track.clientWidth + 4 && getComputedStyle(track).display === 'flex';
      const paint = () => {
        if (counter) counter.textContent = `${index + 1} / ${slides.length}`;
        dots.forEach((d, i) => d.setAttribute('aria-current', i === index ? 'true' : 'false'));
        if (prev) prev.disabled = index === 0;
        if (next) next.disabled = index === slides.length - 1;
      };
      function goTo(i) {
        index = Math.max(0, Math.min(slides.length - 1, i));
        const s = slides[index];
        const left = s.offsetLeft - parseFloat(getComputedStyle(track).paddingLeft || '0');
        track.scrollTo({ left, behavior: reduce.matches ? 'auto' : 'smooth' });
        paint();
      }
      let raf;
      track.addEventListener('scroll', () => {
        if (!isCarousel()) return;
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const pad = parseFloat(getComputedStyle(track).paddingLeft || '0');
          const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 3;
          let best = 0, bestD = Infinity;
          slides.forEach((s, i) => { const d = Math.abs(s.offsetLeft - pad - track.scrollLeft); if (d < bestD) { bestD = d; best = i; } });
          index = atEnd ? slides.length - 1 : best;
          paint();
        });
      }, { passive: true });
      if (prev) prev.addEventListener('click', () => goTo(index - 1));
      if (next) next.addEventListener('click', () => goTo(index + 1));
      track.addEventListener('keydown', (e) => {
        if (!isCarousel()) return;
        if (e.key === 'ArrowRight') { e.preventDefault(); goTo(index + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(index - 1); }
      });
      paint();

      /* ---- lightbox ---- */
      const lb = $('#lightbox');
      if (lb && slides.length) {
        const lbImg = $('[data-lb-img]', lb);
        const lbCount = $('[data-lb-count]', lb);
        const lbCap = $('[data-lb-cap]', lb);
        let li = 0;
        const show = (i) => {
          li = (i + slides.length) % slides.length;
          const s = slides[li];
          const alt = ($('img', s) || {}).alt || '';
          lbImg.classList.add('swap');
          const src = s.dataset.full;
          const img = new Image();
          img.onload = img.onerror = () => { lbImg.src = src; lbImg.alt = alt; lbImg.classList.remove('swap'); };
          img.src = src;
          lbCount.textContent = `${li + 1} / ${slides.length}`;
          lbCap.textContent = alt;
        };
        slides.forEach((s, i) => {
          const b = $('.slide-btn', s);
          if (b) b.addEventListener('click', () => { show(i); openDlg(lb); });
        });
        $('[data-lb-prev]', lb).addEventListener('click', () => show(li - 1));
        $('[data-lb-next]', lb).addEventListener('click', () => show(li + 1));
        lb.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') show(li + 1);
          if (e.key === 'ArrowLeft') show(li - 1);
        });
        lb.addEventListener('close', () => { if (isCarousel()) goTo(li); });
        let sx = null;
        lb.addEventListener('touchstart', (e) => { sx = e.touches[0].clientX; }, { passive: true });
        lb.addEventListener('touchend', (e) => {
          if (sx === null) return;
          const dx = e.changedTouches[0].clientX - sx; sx = null;
          if (Math.abs(dx) > 50) show(li + (dx < 0 ? 1 : -1));
        });
      }
    }

    /* ---- variant picker: every choice resolves to a real Shopify variant ---- */
    const form = $('form[data-type="add-to-cart-form"]', rootEl);
    const dataEl = $('script[data-variants]', rootEl);
    if (!form || !dataEl) return;
    let variants = [];
    try { variants = JSON.parse(dataEl.textContent); } catch (_) { variants = []; }
    const byId = Object.fromEntries(variants.map((v) => [String(v.id), v]));
    const mode = rootEl.dataset.picker;
    const idInput = $('[name="id"]', form);
    const priceEl = $('[data-price]', rootEl);
    const bbPrice = $('[data-bb-price]');
    const addBtn = $('[data-add]', form);
    const bbBtn = $('[data-buybar-add]');
    const sizeErr = $('[data-size-error]', rootEl);
    const strings = window.variantStrings || {};
    const label = cfg.currencyLabel || 'PKR';
    const fmt = (cents) => {
      const n = cents / 100;
      const txt = Number.isInteger(n) ? n.toLocaleString('en-US') : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return label + ' ' + txt;
    };
    const setText = (btn, text) => { const s = btn && btn.querySelector('span'); if (s) s.textContent = text; };

    const wa = $('[data-whatsapp]', rootEl);
    const waUpdate = (v) => {
      if (!wa || !v) return;
      const lines = [wa.dataset.waIntro, 'Product: ' + wa.dataset.waProduct, 'Option: ' + v.title, 'Price: ' + fmt(v.price).replace('\u00a0', ' '), 'Link: ' + wa.dataset.waUrl + '?variant=' + v.id];
      wa.href = wa.dataset.waBase + '?text=' + encodeURIComponent(lines.join('\n'));
    };
    if (wa) wa.addEventListener('click', (e) => {
      if (mode === 'type-size' && idInput && !idInput.value) { e.preventDefault(); form.requestSubmit(); } // shows the "choose a size" message
    });
    const paintVariant = (v, fromCents) => {
      waUpdate(v);
      if (v) {
        if (idInput) idInput.value = v.id;
        if (priceEl) {
          priceEl.textContent = '';
          if (v.compare > v.price) { const s = document.createElement('s'); s.textContent = fmt(v.compare); priceEl.appendChild(s); priceEl.append(' '); }
          priceEl.append(fmt(v.price));
        }
        if (bbPrice) bbPrice.textContent = fmt(v.price);
        [addBtn, bbBtn].forEach((b) => {
          if (!b) return;
          if (v.available) b.removeAttribute('aria-disabled'); else b.setAttribute('aria-disabled', 'true');
          setText(b, v.available ? (strings.addToCart || 'Add to bag') : (strings.soldOut || 'Sold out'));
        });
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', v.id);
          window.history.replaceState({}, '', url.toString());
        } catch (_) { /* ignore */ }
      } else {
        // Stitched chosen but no size yet: the form cannot be submitted (see submit guard below).
        if (idInput) idInput.value = '';
        if (priceEl && fromCents != null) priceEl.textContent = fmt(fromCents);
        if (bbPrice && fromCents != null) bbPrice.textContent = fmt(fromCents);
        [addBtn, bbBtn].forEach((b) => { if (!b) return; b.removeAttribute('aria-disabled'); setText(b, strings.addToCart || 'Add to bag'); });
      }
    };

    if (mode === 'type-size') {
      const typeRadios = $$('input[name="lv-type"]', form);
      const panels = $$('[data-size-panel]', form);
      const sizeRadios = (panel) => $$('input[type="radio"][data-variant]', panel);
      const panelFor = (type) => panels.find((p) => p.dataset.sizePanel === type);

      const resolve = () => {
        const t = (typeRadios.find((r) => r.checked) || {}).value;
        if (t == null) return;
        panels.forEach((p) => { p.hidden = p.dataset.sizePanel !== t; });
        const panel = panelFor(t);
        if (!panel) {
          // Type without sizes (Unstitched): one exact variant
          const v = variants.find((x) => x.option1 === t);
          paintVariant(v || null);
          return;
        }
        const chosen = sizeRadios(panel).find((r) => r.checked);
        if (chosen) { paintVariant(byId[chosen.dataset.variant]); return; }
        const prices = variants.filter((x) => x.option1.split(' - ')[0] === t).map((x) => x.price);
        paintVariant(null, prices.length ? Math.min.apply(null, prices) : null);
      };
      typeRadios.forEach((r) => r.addEventListener('change', () => { if (sizeErr) sizeErr.textContent = ''; resolve(); }));
      $$('input[data-variant]', form).forEach((r) => r.addEventListener('change', () => {
        $$('[data-size-error]', form).forEach((e) => { e.textContent = ''; });
        resolve();
      }));
      resolve();
      window.addEventListener('pageshow', resolve);

      // Block submit unless a concrete variant is selected. Capture phase runs before Dawn's handler.
      form.addEventListener('submit', (e) => {
        if (idInput && idInput.value) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        const t = (typeRadios.find((r) => r.checked) || {}).value;
        const panel = panelFor(t);
        const err = panel ? $('[data-size-error]', panel) : sizeErr;
        if (err) err.textContent = strings.chooseSize || 'Please choose a size first.';
        if (panel) {
          panel.scrollIntoView({ behavior: reduce.matches ? 'auto' : 'smooth', block: 'center' });
          const first = sizeRadios(panel).find((r) => !r.disabled);
          if (first) first.focus({ preventScroll: true });
        }
      }, true);
    } else if (mode === 'select') {
      const sel = $('[data-variant-select]', form);
      if (sel) {
        const run = () => paintVariant(byId[sel.value]);
        sel.addEventListener('change', run);
        run();
      }
    }

    /* ---- sticky buy bar ---- */
    const bar = $('[data-buybar]');
    if (bar && addBtn) {
      const check = () => {
        const show = addBtn.getBoundingClientRect().bottom < 0;
        if (bar.classList.contains('show') === show) return;
        bar.classList.toggle('show', show);
        bar.toggleAttribute('inert', !show);
        bar.setAttribute('aria-hidden', String(!show));
        document.body.classList.toggle('has-buybar', show);
      };
      let t = false;
      window.addEventListener('scroll', () => { if (t) return; t = true; requestAnimationFrame(() => { check(); t = false; }); }, { passive: true });
      window.addEventListener('resize', check);
      check();
    }
  }

  const initAll = () => { $$('dialog').forEach(bindDialog); initCollection(); initProduct(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
